import { Typography } from "@mui/material";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { purpleairColor } from "./purpleairfunctions";
import './purpleairWebpage.css';

const PageHealthRisk = () => {
    function createData(category, value, message, sampleValue) {
        return { category, value, message, sampleValue };
    }

    const rows = [
        createData('safe', '0.0 - 9.0', 'Air quality is satisfactory, little/no risk', 5),
        createData('moderate', '9.1 - 35.4', 'Acceptable, but some risk for sensitive groups', 11),
        createData('Unhealthy for Sensitive Groups', '35.5 - 55.4', 'Sensitive groups may experience health effects', 40),
        createData('Unhealthy', '55.5 - 125.4', 'Everyone may begin to experience effects', 60),
        createData('Very Unhealthy', '125.5 - 225.4', 'Health warnings of emergency conditions', 140),
        createData('Hazardous', '225.5+', 'Serious health effects for everyone', 300)
    ]
    return (<>
        
        <div className='pageContainer' >
            <div className='pageInnerContainer'> 
                <Typography variant="h4" gutterBottom>
                    Health Risks Posed by Crushing Concrete
                </Typography>
                <p>The US EPA PM2.5 Air Quality Index (AQI) is a numerical scale from 0 to 500 used to
                    communicate the level of fine particulate matter (PM2.5) pollution in the air and its associated
                    health risks.</p>
                <TableContainer component={Paper} sx={{ width: '90%', ml: 4, mr: 4 }}>
                    <Table aria-label="simple table">
                        <TableHead style={{ background: '#E5E5E5' }}>
                            <TableRow>
                                <TableCell>AQI Category</TableCell>
                                <TableCell align="center">AQI Value (PM2.5)</TableCell>
                                <TableCell align="left">Health Message</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow
                                    key={row.category}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5' }}
                                >
                                    <TableCell component="th" scope="row" style={{ background: purpleairColor(row.sampleValue) }}>
                                        {row.category}
                                    </TableCell>
                                    <TableCell align="center">{row.value}</TableCell>
                                    <TableCell align="left">{row.message}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <p>PM2.5 refers to fine particulate matter with a diameter of 2.5 micrometers or less.
                    These particles are small enough to penetrate deep into the lungs and even enter
                    the bloodstream, posing significant health risks.</p>

                <Typography variant="h5" gutterBottom>Concrete Dust Exposure</Typography>
                <p>Concrete dust, commonly encountered during construction activities such as cutting, grinding, or mixing, poses
                    significant health risks due to its content of respirable crystalline silica and other hazardous compounds.</p>
                <Typography variant="h6" gutterBottom>Immediate and Short-Term Effects</Typography>
                <ul>
                    <li style={{ paddingBottom: "10px" }}><span style={{ fontWeight: 'bold' }}>Respiratory Irritation:</span> Inhalation of concrete dust can cause immediate symptoms such as coughing,
                        throat irritation, and discomfort in the nose and chest.
                        These symptoms may appear even after brief exposure, especially in poorly ventilated environments.
                    </li>
                    <li>
                        <span style={{ fontWeight: 'bold' }}>Eye and Skin Irritation:</span> Concrete dust can irritate the eyes, causing redness and possible damage to eye tissues.
                        Skin contact may result in rashes, cracking, ulcers, or even chemical burns due to the alkaline nature of cement components.

                    </li>
                </ul>
                <Typography variant="h6" gutterBottom>Long-Term Health Risks</Typography>
                <ul>
                    <li style={{ paddingBottom: "10px" }}><span style={{ fontWeight: 'bold' }}>Silicosis:</span>  Prolonged or intense exposure to silica dust in
                        concrete can lead to silicosis, a severe and incurable lung disease characterized by scarring and loss of lung function.
                        Silicosis can present in acute, accelerated, or chronic forms, depending on the level and duration of exposure.
                        Severe cases can be fatal.
                    </li>
                    <li style={{ paddingBottom: "10px" }}>
                        <span style={{ fontWeight: 'bold' }}>Chronic Obstructive Pulmonary Disease (COPD):</span>  Regular exposure increases the risk of COPD, which
                        includes chronic bronchitis and emphysema, leading to persistent breathing difficulties and reduced quality of life.
                    </li>
                    <li style={{ paddingBottom: "10px" }}>
                        <span style={{ fontWeight: 'bold' }}>Lung Cancer:</span>  Respirable crystalline silica in concrete dust is
                        classified as a human carcinogen. Long-term exposure significantly raises the risk of developing lung cancer.
                    </li>
                    <li style={{ paddingBottom: "10px" }}>
                        <span style={{ fontWeight: 'bold' }}>Other Respiratory Diseases:</span>   Exposure is linked to occupational asthma,
                        pneumonia, and increased susceptibility to respiratory infections. Cement dust can also contribute to
                        restrictive lung diseases and pneumoconiosis.
                    </li>
                    <li style={{ paddingBottom: "10px" }}>
                        <span style={{ fontWeight: 'bold' }}>Oral and Mucosal Effects:</span> Inhalation or oral contact with cement dust can
                        cause inflammation and trauma to the mouth and mucous membranes.
                    </li>
                </ul>
                <Typography variant="h6" gutterBottom>Additional Health Concerns</Typography>
                <ul>
                    <li style={{ paddingBottom: "10px" }}>
                        <span style={{ fontWeight: 'bold' }}>Dermatitis and Allergies:</span> Prolonged skin contact with cement dust
                        can cause dermatitis, allergic reactions, and thickening or ulceration of the skin. Certain compounds,
                        such as chromium, can trigger severe allergies even at low concentrations.
                    </li>
                    <li style={{ paddingBottom: "10px" }}>
                        <span style={{ fontWeight: 'bold' }}>Chemical Burns:</span>The alkaline compounds in cement, particularly calcium oxide,
                        can cause chemical burns on skin and mucous membranes.
                    </li>
                </ul>
                <Typography variant="h6" gutterBottom>Key Prevention Strategies</Typography>
                <ul>
                    <li style={{ paddingBottom: "10px" }}>Use silica-rated respirators and appropriate personal protective equipment (PPE).</li>
                    <li style={{ paddingBottom: "10px" }}>Employ dust suppression techniques, such as wet cutting and local exhaust ventilation.</li>
                    <li style={{ paddingBottom: "10px" }}>Limit exposure duration and ensure regular health monitoring for workers.</li>
                </ul>

            </div>
        </div>
    </>);
}

export default PageHealthRisk;